import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { CircleCheckBold } from "@/components/icons/CircleCheckBold";
import { CircleAlertBold } from "@/components/icons/CircleAlertBold";
import { CircleXBold } from "@/components/icons";

// NOTI ! : react hook form과 사용시 controlled 방식으로 사용할 것. 26.01.19
type InputSize = "large" | "small" | "inline";
type InputStatus = "empty" | "filled" | "focused";

// Context for InputGroup
interface InputGroupContextValue {
  size: InputSize;
  status: InputStatus;
  error: boolean;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  setInputRef: (node: HTMLInputElement | null) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  hasValue: boolean;
  setHasValue: React.Dispatch<React.SetStateAction<boolean>>;
  hasSuffixButton: boolean;
  setHasSuffixButton: React.Dispatch<React.SetStateAction<boolean>>;
}

const InputGroupContext = React.createContext<InputGroupContextValue | null>(
  null,
);

const useInputGroupContext = () => {
  return React.useContext(InputGroupContext);
};

// InputGroup Container 스타일 (외부 flex container)
// items-stretch — suffix 버튼 높이를 입력 박스에 맞춘다
// min-w-0 — input 의 기본 고유폭(약 166px)이 최소폭이 되어 좁은 컨테이너에서 삐져나오는 것 방지
const inputGroupVariants = cva("flex w-full min-w-0 items-stretch");

// InputGroup 내부 Wrapper 스타일 (테두리는 getWrapperBorderClass)
const inputGroupWrapperVariants = cva(
  "flex flex-1 min-w-0 items-center gap-2.5 bg-static-white transition-shadow",
  {
    variants: {
      size: {
        large: "px-4 py-3 rounded-xl",
        small: "px-3 py-2 rounded-lg",
        // min-h — 호출부가 InputGroup 에 높이를 주면(검색 필드 36px) 늘어나야 한다
        inline: "px-2 py-[4.5px] rounded-sm min-h-8",
      },
      disabled: {
        true: "bg-fills-neutral cursor-not-allowed",
        false: "",
      },
    },
    defaultVariants: {
      size: "large",
      disabled: false,
    },
  },
);

/** 입력 박스 테두리 — suffix 버튼이 있어도 4면을 그려 포커스 범위를 입력으로 한정 */
const getWrapperBorderClass = (
  status: InputStatus,
  error: boolean,
  disabled: boolean,
): string => {
  if (disabled) return "inset-border-solid-normal";
  if (error)
    return status === "focused"
      ? "inset-border-error-focus"
      : "inset-border-error";
  return status === "focused"
    ? "inset-border-focus"
    : "inset-border-solid-normal";
};

// InputGroup Component
interface InputGroupProps extends React.ComponentProps<"div"> {
  size?: InputSize;
  error?: boolean;
  disabled?: boolean;
}

function InputGroup({
  className,
  size = "large",
  error = false,
  disabled = false,
  children,
  ...props
}: InputGroupProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(false);
  const [hasSuffixButton, setHasSuffixButton] = React.useState(false);

  const setInputRef = React.useCallback((node: HTMLInputElement | null) => {
    (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
      node;
  }, []);

  const status: InputStatus = React.useMemo(() => {
    if (isFocused) return "focused";
    if (hasValue) return "filled";
    return "empty";
  }, [isFocused, hasValue]);

  const contextValue: InputGroupContextValue = {
    size,
    status,
    error,
    disabled,
    inputRef,
    setInputRef,
    isFocused,
    setIsFocused,
    hasValue,
    setHasValue,
    hasSuffixButton,
    setHasSuffixButton,
  };

  // children에서 InputSuffixButton 분리
  const childArray = React.Children.toArray(children);
  const suffixButton = childArray.find(
    (child) =>
      React.isValidElement(child) &&
      (child.type as React.ComponentType)?.displayName === "InputSuffixButton",
  );
  const otherChildren = childArray.filter(
    (child) =>
      !React.isValidElement(child) ||
      (child.type as React.ComponentType)?.displayName !== "InputSuffixButton",
  );

  return (
    <InputGroupContext.Provider value={contextValue}>
      <div
        data-slot="input-group"
        data-error={error}
        data-disabled={disabled}
        className={cn(inputGroupVariants(), className)}
        {...props}
      >
        {/* 내부 wrapper (border + padding) */}
        <div
          data-slot="input-group-wrapper"
          className={cn(
            inputGroupWrapperVariants({ size, disabled }),
            getWrapperBorderClass(status, error, disabled),
            // 버튼과 맞닿는 모서리는 각지게
            hasSuffixButton && "rounded-r-none",
          )}
        >
          {otherChildren}
        </div>
        {/* SuffixButton은 wrapper 바깥 */}
        {suffixButton}
      </div>
    </InputGroupContext.Provider>
  );
}

// Input variants (standalone용)
const inputVariants = cva(
  [
    "w-full min-w-0 bg-static-white transition-shadow outline-none",
    "placeholder:text-labels-assistive",
    "disabled:bg-fills-neutral disabled:cursor-not-allowed disabled:text-labels-alternative",
    "file:text-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium",
  ],
  {
    variants: {
      size: {
        large: "px-4 py-3 rounded-xl typo-body-1-medium",
        small: "px-3 py-2 rounded-lg typo-body-2-medium",
        inline: "px-2 py-[4.5px] rounded-sm typo-body-2-medium h-8",
      },
    },
    defaultVariants: {
      size: "large",
    },
  },
);

// 텍스트 색상 결정 함수
const getInputTextColor = (status: InputStatus, disabled: boolean): string => {
  if (disabled && status === "filled") return "text-labels-alternative";
  if ((status === "filled" || status === "focused") && !disabled)
    return "text-labels-primary";
  return "text-labels-assistive";
};

// Input Component
interface InputProps
  extends
    Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, size, value, onChange, onFocus, onBlur, ...props },
    ref,
  ) => {
    const context = useInputGroupContext();
    const isInGroup = !!context;

    const ctxSize = context?.size ?? size;
    const status = context?.status ?? "empty";
    const disabled = context?.disabled ?? props.disabled ?? false;
    const setInputRef = context?.setInputRef;
    const setIsFocused = context?.setIsFocused;
    const setHasValue = context?.setHasValue;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused?.(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const trimmed = e.target.value.trim();
      if (e.target.value !== trimmed) {
        const trimmedEvent = {
          ...e,
          target: { ...e.target, value: trimmed },
        };
        onChange?.(trimmedEvent as React.ChangeEvent<HTMLInputElement>);
      }
      setIsFocused?.(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue?.(!!e.target.value);
      onChange?.(e);
    };

    // value prop이 변경될 때 hasValue 업데이트
    React.useEffect(() => {
      if (isInGroup) {
        setHasValue?.(!!value);
      }
    }, [value, setHasValue, isInGroup]);

    // InputGroup 내부에서 사용될 때
    if (isInGroup) {
      return (
        <input
          ref={(node) => {
            // 두 ref 모두 연결
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
            setInputRef?.(node);
          }}
          type={type}
          data-slot="input"
          disabled={disabled}
          value={value} // aws도 type이 password여도 value 보이니까 문제 없지 않을까... 26.01.21
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            "flex-1 bg-transparent outline-none min-w-0",
            "placeholder:text-labels-assistive",
            ctxSize === "large" ? "typo-body-1-medium" : "typo-body-2-medium",
            getInputTextColor(status, disabled),
            disabled && "cursor-not-allowed",
            className,
          )}
          {...props}
        />
      );
    }

    // Standalone으로 사용될 때
    return (
      <input
        ref={ref}
        type={type}
        data-slot="input"
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={cn(
          inputVariants({ size }),
          // 포커스·에러 테두리 유틸 (커스텀 클래스라 focus:/aria-invalid: 변형이 안 먹는다)
          "inset-border-input",
          "text-labels-primary",
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

// InputStartIcon Component
function InputStartIcon({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-start-icon"
      className={cn("shrink-0 flex items-center", className)}
      {...props}
    >
      {children}
    </div>
  );
}

// InputEndIcon Component (상태별 아이콘 자동 전환)
interface InputEndIconProps {
  className?: string;
  showIcon?: boolean;
  customIcon?: React.ReactNode;
}

function InputEndIcon({
  className,
  showIcon = true,
  customIcon,
}: InputEndIconProps) {
  const context = useInputGroupContext();

  if (!context) {
    console.warn("InputEndIcon must be used within InputGroup");
    return null;
  }

  const { size, status, error, disabled, inputRef, setHasValue } = context;

  if (!showIcon) return null;
  if (customIcon) return <>{customIcon}</>;

  const iconSize = size === "large" ? 24 : 20;

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!inputRef.current) return;

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    )?.set;
    nativeInputValueSetter?.call(inputRef.current, "");

    const event = new Event("input", { bubbles: true });
    inputRef.current.dispatchEvent(event);

    setHasValue(false);
    inputRef.current.focus();
  };

  // focused 상태: X 버튼 (clear)
  if (status === "focused") {
    return (
      <button
        type="button"
        onMouseDown={handleClear}
        className={cn("shrink-0 cursor-pointer flex items-center", className)}
        tabIndex={-1}
      >
        <CircleXBold size={iconSize} color="fills-strong" />
      </button>
    );
  }

  // error 상태: 에러 아이콘
  if (error) {
    return (
      <div className={cn("shrink-0 flex items-center", className)}>
        <CircleAlertBold size={iconSize} color="status-urgent" />
      </div>
    );
  }

  // filled 상태 (disabled 아님): 체크 아이콘
  if (status === "filled" && !disabled) {
    return (
      <div className={cn("shrink-0 flex items-center", className)}>
        <CircleCheckBold size={iconSize} color="primary-normal" />
      </div>
    );
  }

  // empty 상태: X 아이콘 (비활성)
  return (
    <div className={cn("shrink-0 flex items-center", className)}>
      <CircleXBold size={iconSize} color="fills-strong" />
    </div>
  );
}

// InputHelperText Component
function InputHelperText({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  const context = useInputGroupContext();
  const size = context?.size ?? "large";

  return (
    <span
      data-slot="input-helper-text"
      className={cn(
        "shrink-0 text-labels-assistive",
        size === "large" ? "typo-label-1-medium" : "typo-label-2-medium",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

// InputSuffixButton Component
interface InputSuffixButtonProps extends React.ComponentProps<"button"> {
  children?: React.ReactNode;
}

function InputSuffixButton({
  className,
  disabled: propDisabled,
  children,
  ...props
}: InputSuffixButtonProps) {
  const context = useInputGroupContext();
  const size = context?.size ?? "large";
  const contextDisabled = context?.disabled ?? false;
  const setHasSuffixButton = context?.setHasSuffixButton;
  const disabled = propDisabled ?? contextDisabled;
  // 좌측을 뺀 3면만 그려 입력 박스와 이어 붙인다. 포커스는 따라가지 않는다
  const borderClass =
    context?.error && !contextDisabled
      ? "inset-border-no-left-error"
      : "inset-border-no-left";

  React.useEffect(() => {
    setHasSuffixButton?.(true);
    return () => setHasSuffixButton?.(false);
  }, [setHasSuffixButton]);

  if (!context) {
    console.warn("InputSuffixButton must be used within InputGroup");
    return null;
  }

  return (
    <button
      type="button"
      data-slot="input-suffix-button"
      disabled={disabled}
      className={cn(
        "relative flex w-fit shrink-0 items-center justify-center whitespace-nowrap",
        borderClass,
        "after:absolute after:inset-0 after:bg-static-black after:opacity-0 after:rounded-[inherit] after:transition-opacity",
        disabled
          ? "bg-fills-neutral text-labels-assistive cursor-not-allowed"
          : "bg-static-white text-primary-normal cursor-pointer hover:after:opacity-[0.06] focus-visible:after:opacity-10 active:after:opacity-[0.14]",
        // 세로 패딩을 주지 않는다 — 높이는 입력 박스가 정하고 버튼은 늘어나 맞춘다
        size === "large"
          ? "typo-body-1-semibold rounded-r-xl px-4"
          : size === "small"
            ? "typo-body-2-semibold rounded-r-lg px-3"
            : "typo-body-2-semibold rounded-r-sm px-2",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

// displayName 설정
InputGroup.displayName = "InputGroup";
InputStartIcon.displayName = "InputStartIcon";
InputEndIcon.displayName = "InputEndIcon";
InputHelperText.displayName = "InputHelperText";
InputSuffixButton.displayName = "InputSuffixButton";

export {
  Input,
  InputGroup,
  InputStartIcon,
  InputEndIcon,
  InputHelperText,
  InputSuffixButton,
  inputVariants,
  inputGroupVariants,
  inputGroupWrapperVariants,
};
